import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

const KEY = "persons";

export function usePersonList(options?: {
	companyId?: string | undefined;
	search?: string | undefined;
	page?: number;
	limit?: number;
}) {
	const page = options?.page ?? 1;
	const limit = options?.limit ?? 20;
	const offset = (page - 1) * limit;
	return useQuery({
		queryKey: [KEY, "list", options],
		queryFn: () =>
			api.get<{ items: Person[]; total: number }>(
				`/api/persons?limit=${limit}&offset=${offset}${options?.companyId ? `&companyId=${options.companyId}` : ""}${options?.search ? `&search=${encodeURIComponent(options.search)}` : ""}`,
			),
	});
}

export function usePersonDetail(id: string) {
	return useQuery({
		queryKey: [KEY, "detail", id],
		queryFn: () => api.get<Person>(`/api/persons/${id}`),
		enabled: !!id,
	});
}

export interface CreatePersonInput {
	companyId: string;
	firstName: string;
	lastName: string;
	title?: string;
	phone?: string;
	mobile?: string;
	email?: string;
	notes?: string;
}

export function useCreatePerson() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (data: CreatePersonInput) => api.post<Person>("/api/persons", data),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
		},
	});
}

interface Person {
	id: string;
	firstName: string;
	lastName: string;
	email: string | null;
	phone: string | null;
	mobile: string | null;
	title: string | null;
	department: string | null;
	recordType: string | null;
	sealInfo: string | null;
	notes: string | null;
	isPrimary: string;
	companyId: string | null;
	ownerId: string | null;
	sapContactCode: number | null;
	sapCardCode: string | null;
	createdAt: string;
	updatedAt: string;
}
